import { DetailModel, FieldModel, ProgramBenchmarkName, ProgramTask } from '@cibo/core'
import { useSaveDetail } from '@cibo/landmanager'
import GridViewIcon from '@mui/icons-material/GridView'
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material'
import {
  GridCellParams,
  GridColumnHeaderParams,
  GridRenderCellParams,
  MuiEvent,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { map, pipe, prop, uniq } from 'ramda'
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InfoNeededCorner } from '../../../../components/TraitTable/InfoNeededCorner'
import { useProgramEngagementContext } from '../../../../context'
import { useProgramModel } from '../../../../queries'
import { PROGRAMS_QUERY_KEY } from '../../../../queries/queryKey'
import { DataEntrySchemaEditingProvider } from '../../Tasks/DataEntry/DataEntrySchemaEditingProvider'
import { FieldTraitTaskEditorTableCell } from './FieldTraitTaskEditorTableCell'
import { TraitDataGrid } from './TraitDataGrid'
import { useFieldTraitColumns } from './useFieldTraitColumns'

type CopiedData =
  | { type: 'cell'; fieldId: string; traitId: string; detail: DetailModel }
  | { type: 'row'; fieldId: string; traits: Array<{ traitId: string; detail: DetailModel }> }
  | { type: 'column'; traitId: string; fields: Array<{ fieldId: string; detail: DetailModel }> }

const removePastingKeys = (keys: string[]) => (prev: Set<string>) => {
  const next = new Set(prev)
  keys.forEach(k => next.delete(k))
  return next
}

export const FieldTraitTaskEditorTable = ({
  task,
}: {
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName }>
}) => {
  const { engagement, fieldModels } = useProgramEngagementContext()
  const programModel = useProgramModel({ programId: engagement.data?.programId })
  const benchmark = programModel.data?.benchmarks?.find(
    ({ benchmark }) => benchmark === task.rule.params?.benchmarkName
  )
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation('@cibo/landmanager/components')

  const gridApiRef = useGridApiRef()
  const focusedColumnHeaderRef = useRef<string | null>(null)
  const columnKeyHandlerRef = useRef<(e: globalThis.KeyboardEvent) => void>()

  const [copied, setCopied] = useState<CopiedData | null>(null)
  const [pastingCells, setPastingCells] = useState<Set<string>>(new Set())

  const queryClient = useQueryClient()
  const saveDetail = useSaveDetail()

  const invalidateEngagement = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS, engagement.data?.id],
    })
    queryClient.invalidateQueries({ queryKey: [PROGRAMS_QUERY_KEY.TASK_DATA] })
  }, [queryClient, engagement.data?.id])

  // Unique trait IDs from benchmark details
  const traitIds: string[] = pipe(
    map(prop('traitId')),
    // @ts-ignore typing pipe is nearly impossible
    uniq
  )(benchmark?.details ?? []) as string[]

  // One row per field
  const rows = (fieldModels.data?.items ?? []).map(fieldModel => {
    const row: { id: string; fieldModel: FieldModel; [key: string]: any } = {
      id: fieldModel.id,
      fieldModel,
    }
    traitIds.forEach(traitId => {
      row[traitId] = fieldModel.findDetail({ traitId })
    })
    return row
  })

  const updateCellValue = (fieldId: string, traitId: string) => {
    const fieldModel = fieldModels.data?.items?.find(f => f.id === fieldId)
    gridApiRef.current?.updateRows([
      { id: fieldId, [traitId]: fieldModel?.findDetail({ traitId }) },
    ])
  }

  const renderTraitCell = useCallback(
    (traitId: string) => (params: GridRenderCellParams) =>
      (
        <FieldTraitTaskEditorTableCell
          {...params}
          row={params.row as { fieldModel: FieldModel; [key: string]: any }}
          value={params.value as DetailModel | undefined}
          task={task}
          onSaveSuccess={() => {
            updateCellValue(params.row.fieldModel.id, traitId)
            invalidateEngagement()
          }}
          isLoading={pastingCells.has(`${params.row.fieldModel.id}:${traitId}`)}
        />
      ),
    [task, updateCellValue, pastingCells, invalidateEngagement]
  )

  const columns = useFieldTraitColumns({ traitIds, renderTraitCell })

  const handleCellCopy = useCallback(
    (params: GridCellParams) => {
      const fieldId = String(params.id)
      if (params.field === 'field') {
        const fieldModel = fieldModels.data?.items?.find(f => f.id === fieldId)
        if (!fieldModel) return
        const traits = traitIds
          .map(t => ({ traitId: t, detail: fieldModel.findDetail({ traitId: t }) }))
          .filter((x): x is { traitId: string; detail: DetailModel } => x.detail != null)
        if (traits.length) setCopied({ type: 'row', fieldId, traits })
      } else {
        const fieldModel = fieldModels.data?.items?.find(f => f.id === fieldId)
        const detail = fieldModel?.findDetail({ traitId: params.field })
        if (detail) setCopied({ type: 'cell', fieldId, traitId: params.field, detail: detail as DetailModel })
      }
    },
    [fieldModels, traitIds]
  )

  const handleCellPaste = useCallback(
    (params: GridCellParams) => {
      if (!copied) return
      const fieldId = String(params.id)
      const targetFieldModel = fieldModels.data?.items?.find(f => f.id === fieldId)
      if (!targetFieldModel) return

      if (params.field === 'field') {
        if (copied.type !== 'row') return
        const details = copied.traits
          .filter(({ traitId: t }) => !targetFieldModel.findDetail({ traitId: t })?.immutable)
          .map(({ traitId: t, detail }) => ({
            traitId: t,
            ...(detail.input !== undefined ? { input: detail.input } : {}),
            ...(detail.result !== undefined ? { result: detail.result } : {}),
          })) as any[]
        if (!details.length) return
        const keys = details.map((d: any) => `${fieldId}:${d.traitId}`)
        setPastingCells(prev => {
          const next = new Set(prev)
          keys.forEach((k: string) => next.add(k))
          return next
        })
        saveDetail
          .mutateAsync({ resourceId: targetFieldModel.resourceId, details })
          .then(() => {
            copied.traits.forEach(({ traitId: t, detail }) => {
              gridApiRef.current?.updateRows([{ id: fieldId, [t]: detail }])
            })
            setPastingCells(removePastingKeys(keys))
            invalidateEngagement()
          })
          .catch(() => setPastingCells(removePastingKeys(keys)))
      } else {
        const traitId = params.field
        if (copied.type !== 'cell') return
        if (copied.traitId !== traitId) {
          enqueueSnackbar("Cannot paste — source and target traits don't match", {
            variant: 'error',
          })
          return
        }
        if (targetFieldModel.findDetail({ traitId })?.immutable) return
        const key = `${fieldId}:${traitId}`
        setPastingCells(prev => new Set(prev).add(key))
        saveDetail
          .mutateAsync({
            resourceId: targetFieldModel.resourceId,
            details: [
              {
                traitId,
                ...(copied.detail.input !== undefined ? { input: copied.detail.input } : {}),
                ...(copied.detail.result !== undefined ? { result: copied.detail.result } : {}),
              },
            ] as any[],
          })
          .then(() => {
            gridApiRef.current?.updateRows([{ id: fieldId, [traitId]: copied.detail }])
            setPastingCells(removePastingKeys([key]))
            invalidateEngagement()
          })
          .catch(() => setPastingCells(removePastingKeys([key])))
      }
    },
    [copied, fieldModels, saveDetail, gridApiRef, enqueueSnackbar, invalidateEngagement]
  )

  const handleCellKeyDown = useCallback(
    (params: GridCellParams, event: MuiEvent<KeyboardEvent>) => {
      focusedColumnHeaderRef.current = null
      if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
        event.defaultMuiPrevented = true
        handleCellCopy(params)
      } else if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        event.defaultMuiPrevented = true
        handleCellPaste(params)
      } else if (event.key === 'Escape') {
        setCopied(null)
      }
    },
    [handleCellCopy, handleCellPaste]
  )

  const handleColumnHeaderClick = useCallback((params: GridColumnHeaderParams) => {
    if (params.field !== 'field') focusedColumnHeaderRef.current = params.field
  }, [])

  // Assigned on every render so the window listener always has a fresh closure
  columnKeyHandlerRef.current = (e: globalThis.KeyboardEvent) => {
    const traitId = focusedColumnHeaderRef.current
    if (!traitId) return

    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      e.preventDefault()
      const fields = (fieldModels.data?.items ?? [])
        .map(f => ({ fieldId: f.id, detail: f.findDetail({ traitId }) }))
        .filter((x): x is { fieldId: string; detail: DetailModel } => x.detail != null)
      if (fields.length) setCopied({ type: 'column', traitId, fields })
      return
    }

    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      e.preventDefault()
      if (!copied || copied.type !== 'column') return
      const targetTraitId = traitId
      const details = copied.fields
        .map(({ fieldId, detail }) => {
          const fieldModel = fieldModels.data?.items?.find(f => f.id === fieldId)
          if (!fieldModel || fieldModel.findDetail({ traitId: targetTraitId })?.immutable)
            return null
          return {
            resourceId: fieldModel.resourceId,
            traitId: targetTraitId,
            ...(detail.input !== undefined ? { input: detail.input } : {}),
            ...(detail.result !== undefined ? { result: detail.result } : {}),
          }
        })
        .filter(Boolean) as Array<{ resourceId: string; traitId: string; [key: string]: any }>
      if (!details.length) return
      const keys = details.map(
        d =>
          `${
            fieldModels.data?.items?.find(f => f.resourceId === d.resourceId)?.id
          }:${targetTraitId}`
      )
      setPastingCells(prev => {
        const next = new Set(prev)
        keys.forEach(k => next.add(k))
        return next
      })
      // Column paste requires one mutation call per field (each has its own resourceId)
      Promise.all(
        details.map(({ resourceId, ...rest }) =>
          saveDetail.mutateAsync({
            resourceId,
            details: [rest] as any[],
          })
        )
      )
        .then(() => {
          copied.fields.forEach(({ fieldId, detail }) => {
            gridApiRef.current?.updateRows([{ id: fieldId, [targetTraitId]: detail }])
          })
          setPastingCells(removePastingKeys(keys))
          invalidateEngagement()
        })
        .catch(() => setPastingCells(removePastingKeys(keys)))
      return
    }

    if (e.key === 'Escape') setCopied(null)
  }

  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => columnKeyHandlerRef.current?.(e)
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const getCellClassName = useCallback(
    (params: GridCellParams) => {
      if (!copied || params.field === 'field') return ''
      const id = String(params.id)
      if (copied.type === 'cell') {
        return id === copied.fieldId && params.field === copied.traitId ? 'cell--copied' : ''
      }
      if (copied.type === 'row') {
        return id === copied.fieldId ? 'cell--copied' : ''
      }
      if (copied.type === 'column') {
        return params.field === copied.traitId ? 'cell--copied' : ''
      }
      return ''
    },
    [copied]
  )

  return (
    <DataEntrySchemaEditingProvider>
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">Workflow Data</Typography>
              <IconButton size="small" disabled aria-label="templates">
                <GridViewIcon />
              </IconButton>
            </Stack>
            <Stack direction="row" alignItems="center" pb={1} justifyContent="flex-end">
              <Box sx={{ width: 16, height: 16, position: 'relative' }}>
                <InfoNeededCorner />
              </Box>
              <Typography variant="caption" paddingLeft={0.5}>
                {t('infoNeeded', { ns: '@cibo/landmanager/components' })}
              </Typography>
            </Stack>
            <TraitDataGrid
              apiRef={gridApiRef}
              rows={rows}
              columns={columns}
              onCellKeyDown={handleCellKeyDown}
              getCellClassName={getCellClassName}
              onColumnHeaderClick={handleColumnHeaderClick}
            />
          </Stack>
        </Paper>
      </Stack>
    </DataEntrySchemaEditingProvider>
  )
}
